/**
 * Utility function to wait for a specified number of milliseconds
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * API fetch wrapper with automatic retry logic
 * 
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @param {number} retryDelay - Delay in milliseconds between retries (default: 5000)
 * @returns {Promise<Response>} The fetch response
 */
export const apiFetch = async (url, options = {}, maxRetries = 2, retryDelay = 5000) => {
  let lastError;
  let attempt = 0;

  while (attempt <= maxRetries) {
    try {
      const response = await fetch(url, options);

      // Handle 401 Unauthorized and 403 Forbidden globally (expired/invalid token), except for login attempts
      if ((response.status === 401 || response.status === 403) && !url.includes('/api/auth/authenticate')) {
        console.warn('Unauthorized or Forbidden (401/403). Redirecting to login...');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminEmail');
        const publicUrl = process.env.PUBLIC_URL || '';
        window.location.href = `${publicUrl}/login`;
        return response;
      }

      // If response is ok or has a client error status (4xx), return immediately
      // Don't retry on client errors as they won't be fixed by retrying
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        return response;
      }

      // For 5xx errors, retry
      if (response.status >= 500) {
        throw new Error(`Server error: ${response.status}`);
      }

      return response;
    } catch (error) {
      lastError = error;

      // If we've exhausted retries, throw the error
      if (attempt >= maxRetries) {
        console.error(`API Fetch failed after ${maxRetries + 1} attempts for ${url}:`, error);
        throw error;
      }

      // Log the retry attempt
      console.warn(`API Fetch failed for ${url}, attempt ${attempt + 1}/${maxRetries + 1}. Retrying in ${retryDelay}ms...`, error);
      
      // Wait before retrying
      await delay(retryDelay);
      attempt++;
    }
  }

  // This should never be reached, but just in case
  throw lastError;
};

/**
 * Helper to make authenticated API requests with automatic retry
 * 
 * @param {string} endpoint - The API endpoint (e.g., '/api/admin/drivers')
 * @param {object} options - Additional fetch options
 * @param {number} maxRetries - Maximum number of retry attempts (default: 2)
 * @param {number} retryDelay - Delay in milliseconds between retries (default: 5000)
 * @returns {Promise<Response>} The fetch response
 */
export const apiRequest = async (endpoint, options = {}, maxRetries = 2, retryDelay = 5000) => {
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
  
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  };

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  const baseUrl = process.env.REACT_APP_API_URL || '';
  // If the endpoint is relative (starts with /) and we have a baseUrl, prepend it.
  // We ensure it doesn't already start with http/https.
  const targetUrl = (endpoint.startsWith('/') && !endpoint.startsWith('http') && baseUrl) 
    ? `${baseUrl.replace(/\/$/, '')}${endpoint}`
    : endpoint;

  return apiFetch(targetUrl, mergedOptions, maxRetries, retryDelay);
};

const api = { apiFetch, apiRequest };
export default api;
